import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Search, 
  Filter, 
  Download, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Activity,
  Users,
  Database,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { api } from '@/services/api';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface AuditLog {
  audit_id: number;
  user_id: number;
  username: string;
  full_name: string;
  action_type: string;
  table_name: string;
  record_id: number;
  old_values: string;
  new_values: string;
  field_changes: string;
  ip_address: string;
  user_agent: string;
  error_reason: string;
  created_at: string;
  action_timestamp: string;
}

interface AuditStatistics {
  actions_by_type: Array<{ action_type: string; count: number }>;
  actions_by_table: Array<{ table_name: string; count: number }>;
  most_active_users: Array<{ username: string; full_name: string; action_count: number }>;
  failed_attempts: number;
}

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const ACTION_BADGES: Record<string, { variant: any; icon: any }> = {
  'INSERT': { variant: 'default', icon: CheckCircle },
  'UPDATE': { variant: 'default', icon: Activity },
  'DELETE': { variant: 'destructive', icon: XCircle },
  'VIEW': { variant: 'secondary', icon: Activity },
  'FAILED_INSERT': { variant: 'destructive', icon: AlertCircle },
  'FAILED_UPDATE': { variant: 'destructive', icon: AlertCircle },
  'FAILED_DELETE': { variant: 'destructive', icon: AlertCircle },
};

export function AuditTrail() {
  const [activeTab, setActiveTab] = useState('logs');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [statistics, setStatistics] = useState<AuditStatistics | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [selectedTable, setSelectedTable] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [limit, setLimit] = useState<number>(100);

  useEffect(() => {
    fetchAuditLogs();
    if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [selectedAction, selectedTable, dateRange, limit, activeTab]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = { limit };
      
      if (selectedAction !== 'all') {
        params.action_type = selectedAction;
      }
      if (selectedTable !== 'all') {
        params.table_name = selectedTable;
      }
      if (dateRange?.from) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange?.to) {
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }

      const response = await api.get<AuditLog[]>('/api/admin/audit', { params });
      
      if (response.success && response.data) {
        setAuditLogs(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea jurnalului de audit');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    setLoading(true);
    
    try {
      const params: any = {};
      
      if (dateRange?.from) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange?.to) {
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }

      const response = await api.get<AuditStatistics>('/api/admin/audit/statistics', { params });
      
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportAuditLogs = async () => {
    try {
      const params: any = { limit: 10000 };
      
      if (selectedAction !== 'all') {
        params.action_type = selectedAction;
      }
      if (selectedTable !== 'all') {
        params.table_name = selectedTable;
      }
      if (dateRange?.from) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange?.to) {
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }

      const response = await api.get<AuditLog[]>('/api/admin/audit', { params });
      
      if (response.success && response.data) {
        // Convert to CSV
        const csv = convertToCSV(response.data);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_log_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error('Error exporting audit logs:', err);
    }
  };

  const convertToCSV = (data: AuditLog[]) => {
    const headers = ['ID', 'Data', 'Utilizator', 'Acțiune', 'Tabel', 'ID record', 'IP', 'Motiv eroare'];
    const rows = data.map(log => [
      log.audit_id,
      format(new Date(log.action_timestamp || log.created_at), 'dd/MM/yyyy HH:mm:ss'),
      log.username,
      log.action_type,
      log.table_name,
      log.record_id,
      log.ip_address,
      log.error_reason || ''
    ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const getActionBadge = (action: string) => {
    const config = ACTION_BADGES[action] || { variant: 'outline', icon: Activity };
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {action}
      </Badge>
    );
  };

  const filteredLogs = auditLogs.filter(log => 
    searchTerm === '' || 
    log.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.table_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.action_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
          <CardTitle>Jurnal de audit</CardTitle>
          <CardDescription>
            Monitorizează toate acțiunile utilizatorilor în sistem
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="logs">Jurnal</TabsTrigger>
              <TabsTrigger value="statistics">Statistici</TabsTrigger>
            </TabsList>

            <div className="flex flex-col md:flex-row gap-4 my-4">
              <div className="flex-1 relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Caută în jurnal..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>

              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Toate acțiunile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate acțiunile</SelectItem>
                  <SelectItem value="INSERT">INSERT</SelectItem>
                  <SelectItem value="UPDATE">UPDATE</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                  <SelectItem value="VIEW">VIEW</SelectItem>
                  <SelectItem value="FAILED_INSERT">FAILED INSERT</SelectItem>
                  <SelectItem value="FAILED_UPDATE">FAILED UPDATE</SelectItem>
                  <SelectItem value="FAILED_DELETE">FAILED DELETE</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Toate tabelele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate tabelele</SelectItem>
                  <SelectItem value="users">Utilizatori</SelectItem>
                  <SelectItem value="patients">Pacienți</SelectItem>
                  <SelectItem value="chd_diagnoses">Diagnostice</SelectItem>
                  <SelectItem value="health_markers">Markeri sănătate</SelectItem>
                  <SelectItem value="maternal_data">Date maternale</SelectItem>
                  <SelectItem value="hospitals">Spitale</SelectItem>
                </SelectContent>
              </Select>

              <DatePickerWithRange
                date={dateRange}
                onDateChange={setDateRange}
              />

              <Button
                variant="outline"
                size="icon"
                onClick={exportAuditLogs}
                disabled={loading}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <TabsContent value="logs">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Ora</TableHead>
                        <TableHead>Utilizator</TableHead>
                        <TableHead>Acțiune</TableHead>
                        <TableHead>Tabel</TableHead>
                        <TableHead>ID Record</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Detalii</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            Nu există înregistrări
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLogs.map((log) => (
                          <TableRow key={log.audit_id}>
                            <TableCell className="text-sm">
                              {log.action_timestamp && !isNaN(Date.parse(log.action_timestamp))
                                ? format(new Date(log.action_timestamp), 'dd/MM/yyyy HH:mm:ss')
                                : 'Recent'}
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">{log.username}</div>
                                <div className="text-xs text-muted-foreground">{log.full_name}</div>
                              </div>
                            </TableCell>
                            <TableCell>{getActionBadge(log.action_type)}</TableCell>
                            <TableCell>{log.table_name}</TableCell>
                            <TableCell>{log.record_id || '-'}</TableCell>
                            <TableCell className="text-xs">{log.ip_address}</TableCell>
                            <TableCell>
                              {log.error_reason && (
                                <Badge variant="destructive" className="text-xs">
                                  {log.error_reason}
                                </Badge>
                              )}
                              {log.field_changes && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    // Show changes modal
                                    console.log('Changes:', JSON.parse(log.field_changes));
                                  }}
                                >
                                  Vezi modificări
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="statistics" className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : statistics ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          Total acțiuni
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {statistics.actions_by_type.reduce((sum, item) => sum + item.count, 0)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Utilizatori activi
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {statistics.most_active_users.length}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Database className="h-4 w-4" />
                          Tabele modificate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {statistics.actions_by_table.length}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Încercări Eșuate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-destructive">
                          {statistics.failed_attempts}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">Acțiuni pe Tip</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={statistics.actions_by_type}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ action_type, count }) => `${action_type}: ${count}`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="count"
                            >
                              {statistics.actions_by_type.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">Acțiuni pe Tabel</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={statistics.actions_by_table.slice(0, 5)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="table_name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium">Utilizatori cei mai activi</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {statistics.most_active_users.map((user, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <div className="font-medium">{user.username}</div>
                              <div className="text-sm text-muted-foreground">{user.full_name}</div>
                            </div>
                            <Badge variant="secondary">
                              {user.action_count} acțiuni
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nu există date statistice disponibile
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      </div>
    </MainLayout>
  );
}